import { Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import type { DivergenceCause } from "@/types/closed-loop.types";
import { CAUSE_HINTS, CAUSE_LABELS } from "./labels";

interface CauseOptionProps {
  cause: DivergenceCause;
  selected: boolean;
  /** O motivo do SERVIDOR quando a causa está vedada; `null` quando está disponível (FR-012). */
  blockedReason: string | null;
  /** Marca a causa que a regra propôs — o "sim" de um clique só. */
  isProposal: boolean;
  onSelect: (cause: DivergenceCause) => void;
}

// Spec 077 (FR-012) — a causa, habilitada ou VEDADA COM O MOTIVO À VISTA.
//
// ⚠️ Causa vedada é EXIBIDA, nunca omitida. Sumir com ela deixaria quem lê procurando uma opção que
// existe e concluindo que a tela está quebrada; exibi-la desabilitada e sem motivo seria pior — a
// pessoa saberia que a opção existe e não saberia por que não pode usá-la. O motivo vem pronto do
// servidor (`blockedCauses[].reason`), porque o motivo É a regra, e a regra mora num lugar só.

export function CauseOption({
  cause,
  selected,
  blockedReason,
  isProposal,
  onSelect,
}: CauseOptionProps) {
  const blocked = blockedReason !== null;

  return (
    <label
      className={cn(
        "flex cursor-pointer gap-2.5 rounded-md border p-3 transition-colors",
        "focus-within:ring-brand-500/40 focus-within:ring-2",
        selected ? "border-brand-500 bg-brand-50" : "border-border-subtle hover:bg-bg-muted-rd",
        blocked && "cursor-not-allowed border-border-subtle bg-bg-muted-rd opacity-70"
      )}
    >
      <input
        type="radio"
        name="divergence-cause"
        className="mt-0.5 h-4 w-4 shrink-0 accent-brand-600"
        value={cause}
        checked={selected}
        disabled={blocked}
        onChange={() => onSelect(cause)}
      />
      <span className="min-w-0 space-y-1">
        <span className="flex flex-wrap items-center gap-1.5">
          <span className="text-[13px] font-medium text-text-primary">{CAUSE_LABELS[cause]}</span>
          {isProposal ? (
            <span className="border-brand-500/30 rounded-full border bg-brand-50 px-2 py-px text-[11px] font-medium text-brand-700">
              proposta pela regra
            </span>
          ) : null}
        </span>
        <span className="block text-[12px] text-text-secondary">{CAUSE_HINTS[cause]}</span>
        {blocked ? (
          // Cor + ícone + texto: a indisponibilidade nunca é comunicada só pelo cinza.
          <span className="flex items-start gap-1.5 text-[12px] text-text-secondary">
            <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
            <span>{blockedReason}</span>
          </span>
        ) : null}
      </span>
    </label>
  );
}
